import {
  Completion,
  CompletionContext,
  CompletionResult,
  startCompletion,
} from '@codemirror/autocomplete';
import { EditorView } from '@codemirror/view';

import type { IntlProps } from '../utils/render-node.js';
import { render } from '../utils/render-node.js';
import type {
  AutocompleteProvider,
  AutocompletionResult,
  Suggestion,
} from './autocompletion-types.js';

export type { AutocompleteProvider, AutocompletionResult, Suggestion };

/** Apply completion and reopen completions */
async function applyAndReopen(
  view: EditorView,
  from: number,
  to: number,
  completion: Completion,
  apply: Completion['apply'],
  reopen: boolean | ((suggestion: Completion) => Promise<boolean>),
) {
  if (apply === undefined || typeof apply === 'string') {
    const value = apply || completion.label;
    view.dispatch({
      changes: { from, to, insert: value },
      selection: { anchor: from + value.length },
      userEvent: 'input.complete',
    });
  } else {
    apply(view, completion, from, to);
  }
  if (typeof reopen === 'function' ? await reopen(completion) : reopen) {
    startCompletion(view);
  }
}

export const autocompletionExtension = (
  autocompleteProvider: AutocompleteProvider,
  intlProps: IntlProps,
): ((ctx: CompletionContext) => Promise<CompletionResult | null>) => {
  return async (ctx: CompletionContext): Promise<CompletionResult | null> => {
    const result = await autocompleteProvider(ctx);
    if (!result) {
      return null;
    }
    return {
      ...result,
      options: result.suggestions.map((s, i) => ({
        ...s,
        info: s.popup
          ? (completion) => render(completion, s.popup, intlProps) as Node
          : undefined,
        apply: (view, completion, from, to) =>
          applyAndReopen(
            view,
            from,
            to,
            completion,
            s.apply,
            result.reopen ?? false,
          ),
        ...(result.keepOrder && { boost: 99 - i }),
      })),
    };
  };
};
